/**
 * Charts, hand-rolled as SVG.
 *
 * Deliberately not a charting library. The board needs sparklines, grouped bars,
 * one stacked proportion bar and a two-series line — roughly 200 lines of SVG.
 * A library to draw that is ~150 kB of JS on a page that repaints every 10 s,
 * and it would still need overriding to match the palette. Revisit only if a
 * panel needs interaction a library actually provides, like brush-to-zoom.
 *
 * Every chart takes an explicit `label` and renders role="img", because a wall
 * display is also read by people using screen readers.
 */

import { n } from './format'

const clamp = (v: number, lo = 0, hi = 100) => Math.max(lo, Math.min(hi, v))

// Guard audit (mutation-tested 2026-07-30). LOAD-BEARING — removing any of these
// puts NaN or an out-of-range value into the DOM, and a test fails:
//   * StatusRing  `of > 0 ? … : 0`         — 0-of-0 on first boot
//   * StackedBar  `… || 1`                 — an all-zero snapshot
//   * StackedBar  `clamp(Math.max(0, …))`  — a negative segment
//   * Sparkline   `values.length < 2`      — a single sample
// DEFENSIVE ONLY — unreachable in output, no test can cover them, kept because
// they cost nothing:
//   * GroupedBars `Math.max(days.length, 1)` — the map never runs when empty
//   * GroupedBars `a[i] ?? 0`                — `av > 0` already rejects undefined

/** Inline trend, no axes. For "is this rising or falling" at a glance. */
export function Sparkline({ values, state = 'flow', height = 26, label }: {
  values: readonly number[]; state?: 'flow' | 'ok' | 'warn' | 'crit'
  height?: number; label: string
}) {
  if (values.length < 2) return <div style={{ height }} />
  // max is clamped to >= 1 and min to <= 0, so span >= 1 and the division below
  // cannot collapse. A `|| 1` fallback here was unreachable, and a test that
  // claimed to exercise it survived removing it — dead guards invite dead tests.
  const max = Math.max(...values, 1)
  const min = Math.min(...values, 0)
  const span = max - min
  const pts = values
    .map((v, i) => {
      const x = (i / (values.length - 1)) * 100
      const y = height - 2 - ((v - min) / span) * (height - 4)
      return `${x.toFixed(2)},${y.toFixed(2)}`
    })
    .join(' ')
  return (
    <svg viewBox={`0 0 100 ${height}`} height={height} width="100%"
         preserveAspectRatio="none" role="img" aria-label={label}>
      <polyline points={pts} fill="none" strokeWidth={1.5}
                stroke={`var(--${state})`} vectorEffect="non-scaling-stroke" />
    </svg>
  )
}

/**
 * Grouped inserts-vs-updates bars, one pair per day.
 *
 * Zero is drawn as a visible baseline tick rather than nothing, so "no writes
 * at all" is distinguishable from "no data" — which is exactly the difference
 * between a working index and the one that had been dead 11 days.
 */
export function GroupedBars({ days, a, b, aLabel, bLabel, label, tooltip }: {
  days: readonly string[]
  a: readonly number[]; b: readonly number[]
  aLabel: string; bLabel: string; label: string
  /** Per-day hover text (e.g. count and share for each series) — rendered as
   *  a native SVG `<title>` over that day's whole group, so it works with no
   *  chart library and no JS-driven tooltip state. */
  tooltip?: (i: number) => string
}) {
  const H = 108, W = 520, PAD_L = 6, BASE = H - 22
  const max = Math.max(...a, ...b, 1)
  const slot = (W - PAD_L) / Math.max(days.length, 1)
  const bw = Math.max(4, Math.min(20, slot / 2.6))
  const h = (v: number) => (v / max) * (BASE - 8)

  return (
    <div style={{ overflowX: 'auto' }}>
      <svg viewBox={`0 0 ${W} ${H}`} width="100%" height={H} role="img" aria-label={label}>
        {[0.25, 0.5, 0.75].map((f) => (
          <line key={f} x1={PAD_L} x2={W} y1={BASE - (BASE - 8) * f}
                y2={BASE - (BASE - 8) * f} stroke="var(--line2)" />
        ))}
        <line x1={PAD_L} x2={W} y1={BASE} y2={BASE} stroke="var(--line)" />
        {days.map((d, i) => {
          const x = PAD_L + i * slot + slot / 2
          const av = a[i] ?? 0
          const bv = b[i] ?? 0
          return (
            <g key={d}>
              {av > 0
                ? <rect x={x - bw - 1} y={BASE - h(av)} width={bw} height={h(av)} fill="var(--ins)" />
                : <line x1={x - bw - 1} x2={x - 1} y1={BASE} y2={BASE} stroke="var(--crisis)" strokeWidth={1.5} opacity={0.55} />}
              {bv > 0
                ? <rect x={x + 1} y={BASE - h(bv)} width={bw} height={h(bv)} fill="var(--flow)" />
                : <line x1={x + 1} x2={x + bw + 1} y1={BASE} y2={BASE} stroke="var(--crisis)" strokeWidth={1.5} opacity={0.55} />}
              <text x={x} y={H - 6} textAnchor="middle" fontSize={9}
                    fill="var(--dim2)" fontFamily="var(--mono)">{d.slice(8)}</text>
              {tooltip && (
                // An invisible, full-slot-width hit target — a bare bar is too
                // thin (a few px at max(4,...) width) to reliably catch the
                // pointer, and a <title> on a non-painted <g> is not a
                // guaranteed hover target in every browser. This always is.
                <rect x={x - slot / 2} y={0} width={slot} height={BASE}
                      fill="transparent" pointerEvents="all">
                  <title>{tooltip(i)}</title>
                </rect>
              )}
            </g>
          )
        })}
      </svg>
      <div className="chart-key mono">
        <span><i style={{ background: 'var(--ins)' }} />{aLabel}</span>
        <span><i style={{ background: 'var(--flow)' }} />{bLabel}</span>
      </div>
    </div>
  )
}

/** One stacked proportion bar. Segments are clamped and normalised so bad data
 *  cannot render a bar wider than its track. */
export function StackedBar({ segments, label }: {
  segments: readonly { value: number; state: 'ok' | 'flow' | 'warn' | 'crit'; name: string }[]
  label: string
}) {
  const total = segments.reduce((s, x) => s + Math.max(0, x.value), 0) || 1
  return (
    <>
      <div className="stack" role="img" aria-label={label}>
        {segments.map((s) => (
          <i key={s.name} className={s.state}
             style={{ width: `${clamp((Math.max(0, s.value) / total) * 100)}%` }} />
        ))}
      </div>
      <div className="chart-key mono">
        {segments.map((s) => (
          <span key={s.name}>
            <i className={s.state} />{s.name} <b>{s.value.toLocaleString('en-US')}</b>
          </span>
        ))}
      </div>
    </>
  )
}

/** Status ring. `of` may be 0 — a board with nothing configured must not divide by it. */
export function StatusRing({ value, of, state, caption }: {
  value: number; of: number; state: 'ok' | 'warn' | 'crit'; caption: string
}) {
  const R = 28, C = 2 * Math.PI * R
  const frac = of > 0 ? clamp(value / of, 0, 1) : 0
  return (
    <svg width={68} height={68} viewBox="0 0 68 68" role="img"
         aria-label={`${caption}: ${value} of ${of}`}>
      <circle cx={34} cy={34} r={R} fill="none" stroke="var(--line)" strokeWidth={6} />
      <circle cx={34} cy={34} r={R} fill="none" stroke={`var(--${state})`} strokeWidth={6}
              strokeLinecap="round" strokeDasharray={C}
              strokeDashoffset={C * (1 - frac)} transform="rotate(-90 34 34)" />
      <text x={34} y={32} textAnchor="middle" fontSize={15} fill="var(--ink2)"
            fontFamily="var(--mono)">{value}/{of}</text>
      <text x={34} y={44} textAnchor="middle" fontSize={7} letterSpacing={1.1}
            fill="var(--dim)" fontFamily="var(--sans)">{caption.toUpperCase()}</text>
    </svg>
  )
}

const isoDay = (d: string) =>
  (/^\d{8}$/.test(d) ? `${d.slice(0, 4)}-${d.slice(4, 6)}-${d.slice(6)}` : d)

/**
 * One neutral bar per day, oldest first — a single daily series (the Scoring
 * tab's channels scored per UTC day). `day` is `YYYYMMDD` or ISO.
 *
 * A DAY WITH NO RECORD IS NOT A ZERO. `n: null` means nothing was recorded for
 * that day at all (no telemetry hash), which is "cannot state", so it is drawn
 * as a dashed tick in the cannot-state ink (`--unknown`) — never as an empty
 * bar, which would read as "scored nothing". Bars are `--dim`: a series with no
 * channel hue (DESIGN.md) spends none.
 */
export function DayBars({ days, label, unit }: {
  days: ReadonlyArray<{ day: string; n: number | null }>
  label: string
  /** the noun in each day's hover text: "1,200 scored" */
  unit: string
}) {
  const H = 96, W = 520, BASE = H - 18
  const max = Math.max(1, ...days.map((d) => d.n ?? 0))
  const slot = W / Math.max(days.length, 1)
  const bw = Math.max(4, Math.min(28, slot * 0.6))
  return (
    <svg viewBox={`0 0 ${W} ${H}`} width="100%" height={H} role="img" aria-label={label}>
      <line x1={0} x2={W} y1={BASE} y2={BASE} stroke="var(--line)" />
      {days.map((d, i) => {
        const x = i * slot + slot / 2
        const h = d.n ? (d.n / max) * (BASE - 6) : 0
        return (
          <g key={d.day}>
            {d.n === null
              ? <line x1={x - bw / 2} x2={x + bw / 2} y1={BASE - 1} y2={BASE - 1}
                      stroke="var(--unknown)" strokeWidth={1.5} strokeDasharray="2 2" />
              : <rect x={x - bw / 2} y={BASE - h} width={bw} height={h} fill="var(--dim)" />}
            <text x={x} y={H - 5} textAnchor="middle" fontSize={9}
                  fill="var(--dim2)" fontFamily="var(--mono)">{d.day.slice(-2)}</text>
            {/* Full-slot hit target so the hover works on a thin bar (same
                reason as GroupedBars' tooltip rect). */}
            <rect x={x - slot / 2} y={0} width={slot} height={BASE} fill="transparent" pointerEvents="all">
              <title>{`${isoDay(d.day)} · ${d.n === null ? 'no record — cannot state' : `${n(d.n)} ${unit}`}`}</title>
            </rect>
          </g>
        )
      })}
    </svg>
  )
}
